import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { trackBetaApplication, trackDashboardCta } from "@/lib/analytics";

const rotatingPersonas = [
  "Marcus Delgado · Austin, TX · $26,412 LTV",
  "Priya Rao · London, UK · $21,890 LTV",
  "Dominic Reyes · Brooklyn, NY · $25,891 LTV",
  "Elena Moretti · Milan, IT · $22,104 LTV",
  "James Okafor · Lagos, NG · $18,740 LTV",
  "Naomi Patel · Denver, CO · $19,385 LTV",
  "Sarah Kim · Seoul, KR · $17,305 LTV",
  "Lucas Vance · Nashville, TN · $19,339 LTV",
  "Hayden Ross · Portland, OR · $16,875 LTV",
  "Hana Takahashi · Tokyo, JP · $14,920 LTV",
  "+ 5,990 more, named",
];

export default function Hero() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setI((prev) => (prev + 1) % rotatingPersonas.length),
      2800
    );
    return () => clearInterval(id);
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center justify-center overflow-hidden paper-grain"
    >
      <div className="absolute inset-0 subtle-grid opacity-40" />
      <div className="absolute inset-x-0 top-0 h-[40vh] bg-gradient-to-b from-[#030305] via-transparent to-transparent pointer-events-none" />
      <div className="absolute inset-x-0 bottom-0 h-[30vh] bg-gradient-to-t from-[#030305] to-transparent pointer-events-none" />

      <div className="relative z-10 w-full max-w-[1200px] mx-auto px-6 lg:px-12 pt-28 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="text-center"
        >
          <h1
            className="font-display text-white text-[44px] sm:text-[68px] lg:text-[96px] font-semibold leading-[0.98] tracking-[-0.025em] mb-8 max-w-[1120px] mx-auto"
            data-testid="hero-headline"
          >
            Know the 10% of customers{" "}
            <span className="hero-glow text-[#D4FF4A] whitespace-normal">
              who drive half your revenue.
            </span>
          </h1>

          <p
            className="font-display text-[#E4E4E7] text-[22px] sm:text-[28px] lg:text-[34px] font-medium leading-[1.2] max-w-[820px] mx-auto mb-10"
            data-testid="hero-subhead"
          >
            Then go find 10,000 more just like them.
          </p>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-8 rounded-full bg-[#D4FF4A]/10 border border-[#D4FF4A]/25">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4FF4A]" />
            <span className="font-mono text-[#D4FF4A] text-[11px] tracking-[0.08em] uppercase">
              Five beta spots · $999 one time
            </span>
          </div>

          <div
            className="h-8 mb-14 flex items-center justify-center gap-2.5"
            data-testid="hero-rotator"
          >
            <span
              aria-hidden
              className="w-1.5 h-1.5 rounded-full bg-[#D4FF4A] shadow-[0_0_10px_rgba(212,255,74,0.7)] animate-pulse"
            />
            <AnimatePresence mode="wait">
              <motion.span
                key={i}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.35 }}
                className="font-mono text-[#D4D4D8] text-[13px] tracking-[0.02em]"
              >
                {rotatingPersonas[i]}
              </motion.span>
            </AnimatePresence>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-16">
            <a
              href="#dashboard"
              onClick={() => trackDashboardCta("hero")}
              className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#D4FF4A] text-[#030305] text-[15px] font-semibold rounded-full hover:bg-white transition-colors duration-200"
              data-testid="hero-cta-primary"
            >
              See the dashboard
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className="transition-transform group-hover:translate-x-0.5"
              >
                <path
                  d="M3 8h10M9 4l4 4-4 4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
            <a
              href="https://tidycal.com/nick-christensen/30-minute-meeting"
              onClick={() => trackBetaApplication("hero")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-transparent text-white text-[15px] font-medium rounded-full border border-white/16 hover:border-white/40 hover:bg-white/5 transition-all duration-200"
              data-testid="hero-cta-secondary"
            >
              Apply for a beta spot
            </a>
          </div>

          <ProofStrip />
        </motion.div>
      </div>
    </section>
  );
}

function ProofStrip() {
  const items = [
    { value: "01", label: "Rich customer avatar" },
    { value: "02", label: "Private buyer dashboard" },
    { value: "03", label: "30 / 60 / 90-day plan" },
  ];
  return (
    <div
      className="inline-flex flex-wrap items-center justify-center gap-x-8 gap-y-4 text-sm text-[#A1A1AA]"
      data-testid="hero-proof-strip"
    >
      {items.map((item, i) => (
        <div key={item.label} className="flex items-center gap-2">
          <span className="font-mono text-white/90 font-medium">{item.value}</span>
          <span>{item.label}</span>
          {i < items.length - 1 && (
            <span aria-hidden className="hidden sm:inline text-white/20 ml-5">·</span>
          )}
        </div>
      ))}
    </div>
  );
}
